import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectIsInDineIn, selectTableSession, selectGuestToken, clearTableSession, syncSessionTable } from './tableSessionSlice';
import {
  useCreateTablePaymentMutation,
  useGetTablePaymentQuery,
  useGetTableSessionQuery,
  usePostTableOptionAddonsMutation,
} from './tableSessionApi';
import type { OrderLine, TableOrder } from './tableSessionApi';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import AddOptionsSheet, { type AddOptionsPayload } from '@/features/menu/AddOptionsSheet';
import { addableGroups, carriedByLine, type SentLine } from '@/features/menu/optionAddons';
import Drawer from '@/components/ui/Drawer';
import Price, { formatPrice } from '@/components/ui/Price';
import BuffetHud from './BuffetHud';
import { useTableSessionPollInterval } from './useTableSessionPollInterval';

interface Props {
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------
type StatusVariant = 'default' | 'pending' | 'rejected';

interface StatusInfo {
  labelKey: string;
  variant: StatusVariant;
}

function resolveStatus(status: string): StatusInfo {
  switch (status) {
    case 'pending_validation':
      return { labelKey: 'tablePanel.statusPendingValidation', variant: 'pending' };
    case 'cancelled':
      return { labelKey: 'tablePanel.statusCancelled', variant: 'rejected' };
    case 'received':
      return { labelKey: 'tablePanel.statusReceived', variant: 'default' };
    case 'preparing':
      return { labelKey: 'tablePanel.statusPreparing', variant: 'default' };
    case 'ready':
      return { labelKey: 'tablePanel.statusReady', variant: 'default' };
    case 'handed_over':
      return { labelKey: 'tablePanel.statusHandedOver', variant: 'default' };
    default:
      // Fix 13: unknown statuses get a neutral label + a console.warn (not 'Reçue')
      console.warn(`[TablePanel] unknown order status: "${status}"`);
      return { labelKey: 'tablePanel.statusUnknown', variant: 'default' };
  }
}

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

/** SC-1739 — le serveur refuse le reste ; ici, ne proposer que ce qu'il acceptera. */
const ADDON_OPEN_STATUSES = new Set(['received', 'preparing', 'ready']);

function toSentLine(line: OrderLine): SentLine {
  return {
    uuid: line.uuid ?? '',
    qte: line.qte,
    options: line.options,
    addonOfItemUuid: line.addon_of_item_uuid,
    addonOptionUuid: line.addon_option_uuid,
  };
}

// ---------------------------------------------------------------------------
// Paiement autonome de la table (QR sur place, Stancer)
// spec 2026-08-28-dine-in-qr-self-payment-stancer.md
// ---------------------------------------------------------------------------

/**
 * Le bloc « Régler ma table », au pied du panneau.
 *
 * `enabled` vient ENTIÈREMENT du serveur (drapeau d'activation + fournisseur
 * Stancer + clés en base) : le front ne recompose jamais cette décision, il
 * n'affiche rien tant que le serveur n'a pas dit oui. Le POST est de toute
 * façon refait derrière la même porte — cacher un bouton n'a jamais empêché
 * personne de poster.
 */
function TablePaymentSection({
  guestToken,
  tableToken,
  active,
  mode,
}: {
  guestToken: string;
  tableToken: string;
  active: boolean;
  mode: 'ordering' | 'buffet' | undefined;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const { data } = useGetTablePaymentQuery(
    { guestToken },
    // `active` = tiroir ouvert ET onglet visible. Un sondage inconditionnel
    // faisait 12 requêtes/min PAR téléphone sur un quota partagé par toute la
    // table : deux convives suffisaient à faire disparaître le bouton derrière
    // des 429. Même garde que le panneau lui-même (« Fix 11 »), et deux fois
    // plus lent — le montant dû ne bouge qu'à chaque tournée.
    { skip: !guestToken || !active, pollingInterval: active ? 10000 : 0 },
  );
  const [createPayment, { isLoading }] = useCreateTablePaymentMutation();

  if (!data?.enabled) return null;

  const dueCents = Math.round(parseFloat(data.due_total) * 100);
  const paidCents = data.paid_total ? Math.round(parseFloat(data.paid_total) * 100) : 0;
  const pending = data.pending;
  // Ne promettre la fermeture que la ou elle arrive : sans mode serveur la
  // table reste ouverte apres paiement (il n'y aurait personne pour la
  // rouvrir). Champ absent = back pas encore deploye, on garde l'ancienne
  // regle plutot que de faire disparaitre l'avertissement.
  const willLock = data.ordering_will_lock ?? mode === 'ordering';

  if (data.is_paid && dueCents <= 0) {
    return (
      <div
        data-testid="table-payment-paid"
        role="status"
        className="border-t-2 border-ink px-4 py-3 bg-paper"
      >
        <p className="m-0 font-display text-base text-green-700">{t('tablePayment.paidBadge')}</p>
        {willLock && (
          <p className="m-0 font-mono text-xs text-ink/60">{t('tablePayment.paidHint')}</p>
        )}
      </div>
    );
  }

  if (dueCents <= 0) return null;

  // Le montant figé chez Stancer à l'ouverture du paiement en cours. S'il ne
  // vaut plus le dû (une tournée est arrivée, le comptoir a encaissé), le
  // reprendre débiterait autre chose que ce que l'écran annonce.
  const pendingCents = pending ? Math.round(parseFloat(pending.amount_ttc) * 100) : null;
  const resumable = pending?.redirect_url != null && pendingCents === dueCents;

  const go = async () => {
    setError('');
    // Toujours par le POST, reprise comprise (SC-1725) : c'est le serveur qui
    // relit le paiement ouvert chez Stancer et décide — page encore bonne, il
    // la rend ; page morte (3DS abandonné) ou montant dépassé, il la remplace.
    // Rouvrir ici `pending.redirect_url` renvoyait le convive sur une page
    // Stancer qui n'affichait plus que « Une erreur est survenue ».
    try {
      const attempt = await createPayment({ guestToken, tableToken }).unwrap();
      if (attempt.redirect_url) {
        window.location.assign(attempt.redirect_url);
        return;
      }
      setError(t('tablePayment.errorGeneric'));
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'NOTHING_DUE') setError(t('tablePayment.errorNothingDue'));
      else if (code === 'AMOUNT_CHANGED') setError(t('tablePayment.errorAmountChanged'));
      // Le premier convive attend encore la reponse de Stancer : dire « le
      // montant a changé » serait faux, il n'a pas bougé.
      else if (code === 'PAYMENT_IN_PROGRESS') setError(t('tablePayment.errorInProgress'));
      else setError(t('tablePayment.errorGeneric'));
    }
  };

  return (
    <div className="border-t-2 border-ink px-4 py-3 bg-paper space-y-2">
      {/* Une table qui a paye puis rappele n'affichait que son reste : les
          100 EUR deja regles disparaissaient de l'ecran de celui qui venait de
          les payer. Les deux chiffres, le regle d'abord, le reste ensuite. */}
      {paidCents > 0 && (
        <div
          data-testid="table-payment-paid-total"
          className="flex items-center justify-between font-mono text-xs text-ink/70"
        >
          <span>{t('tablePayment.paidLabel')}</span>
          {/* SC-959 : meme regle que <Price>, ce montant-ci ne passe pas par lui. */}
          <span className="whitespace-nowrap">{formatPrice(paidCents)}</span>
        </div>
      )}
      <div
        data-testid="table-payment-due"
        className="flex items-center justify-between font-display text-base"
      >
        <span>{t('tablePayment.dueLabel')}</span>
        <Price amountCents={dueCents} />
      </div>
      {/* Spec 2026-08-28 paid-table-ordering-lock §4 — le dire AVANT, pas apres.
          Pas de modale de confirmation : la page Stancer en est deja une, un
          clic de plus n'est qu'un point de fuite. Hors mode `ordering` le
          verrou n'existe pas (un buffet EST une suite de tournees apres
          paiement), donc la phrase serait fausse. */}
      {willLock && (
        <p data-testid="table-payment-lock-notice" className="m-0 font-mono text-xs text-ink/70">
          {t('tablePayment.lockNotice')}
        </p>
      )}
      {error && (
        <p role="alert" data-testid="table-payment-error" className="m-0 font-mono text-xs text-sale-red">
          {error}
        </p>
      )}
      <button
        type="button"
        data-testid="table-payment-cta"
        onClick={go}
        disabled={isLoading}
        className="w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm disabled:opacity-50"
      >
        {isLoading ? '…' : resumable ? t('tablePayment.resume') : t('tablePayment.cta')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TablePanel
// ---------------------------------------------------------------------------
export default function TablePanel({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const dispatch = useAppDispatch();

  const isInDineIn = useAppSelector(selectIsInDineIn);
  const session = useAppSelector(selectTableSession);
  const guestToken = useAppSelector(selectGuestToken);

  // Fix 11: visibility-gated polling interval
  const [isVisible, setIsVisible] = useState(!document.hidden);
  useEffect(() => {
    const handler = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const tableSessionPollingInterval = useTableSessionPollInterval();

  const { data: currentData, error: pollError } = useGetTableSessionQuery(
    {
      restaurantUuid: restaurantUuid ?? '',
      sessionUuid: session?.sessionUuid ?? '',
      guestToken: guestToken ?? '',
    },
    {
      skip: !isInDineIn || !guestToken || !session,
      // Fix 11 : onglet caché = 0, aucun sondage. DF-0152 : sinon la cadence
      // s'essouffle depuis la dernière action du convive (5 s, 15 s, puis 30 s).
      pollingInterval: tableSessionPollingInterval,
    },
  );

  // SC-1739 — pas de carte chargée tant que le panneau est fermé.
  const { data: menu } = useGetMenuQuery(restaurantUuid ?? '', { skip: !open || !restaurantUuid });
  const [postOptionAddons] = usePostTableOptionAddonsMutation();
  const [addOptionsTarget, setAddOptionsTarget] = useState<{ line: OrderLine; idempotencyKey: string } | null>(null);
  // Le convive doit voir que c'est parti : la nouvelle tournée tombe souvent sous le
  // pli, et rouvrir la fenêtre enverrait une seconde tournée pour de bon.
  const [addOptionsSentKey, setAddOptionsSentKey] = useState<string | null>(null);
  const roundsListRef = useRef<HTMLDivElement>(null);
  const orderCount = currentData?.orders?.length ?? 0;
  useEffect(() => {
    if (!open) setAddOptionsSentKey(null);
  }, [open]);
  useEffect(() => {
    if (!addOptionsSentKey) return;
    const list = roundsListRef.current;
    list?.scrollTo?.({ top: list.scrollHeight });
  }, [addOptionsSentKey, orderCount]);

  // Fix 4: on 401/403 poll error → clear session and stop rendering
  useEffect(() => {
    if (!pollError) return;
    const status = (pollError as { status?: number }).status;
    if (status === 401 || status === 403) {
      if (restaurantUuid) {
        dispatch(clearTableSession(restaurantUuid));
      }
    }
  }, [pollError, restaurantUuid, dispatch]);

  // SC-355: after a back-office table transfer, the join-time tableNumber in the
  // Redux session is stale. The live poll returns the session's current table —
  // sync it back so the "Ma table" chip and menu banner (which read the session)
  // stop showing the old table. Historical order data is never modified.
  useEffect(() => {
    const live = currentData?.table_number;
    if (live == null || !session) return;
    if (String(live) !== String(session.tableNumber)) {
      dispatch(syncSessionTable(live));
    }
  }, [currentData, session, dispatch]);

  // Do not render at all when no session active
  if (!isInDineIn || !session) return null;

  const mode = currentData?.mode ?? session.mode;
  const tableNumber = currentData?.table_number ?? session.tableNumber;
  const covers = currentData?.covers ?? session.covers;
  const roundCount = currentData?.round_count ?? session.roundCount;
  const cooldown =
    currentData?.cooldown_seconds_remaining ?? session.cooldownSecondsRemaining ?? 0;
  const isBuffet = mode === 'buffet';

  const orders = (currentData?.orders ?? []) as TableOrder[];

  // Running total: use server-computed value (source of truth)
  const runningTotalCents = currentData?.running_total
    ? Math.round(parseFloat(currentData.running_total) * 100)
    : 0;

  // Group orders by round number
  const roundMap = new Map<number, TableOrder[]>();
  for (const order of orders) {
    const key = order.round_number;
    if (!roundMap.has(key)) roundMap.set(key, []);
    roundMap.get(key)!.push(order);
  }
  const sortedRounds = [...roundMap.keys()].sort((a, b) => a - b);

  const sentLines = orders.flatMap((order) => order.lines.map(toSentLine));
  const productsById = new Map((menu?.items ?? []).map((product) => [product.id, product]));
  const addOptionsProduct = addOptionsTarget?.line.product_uuid
    ? productsById.get(addOptionsTarget.line.product_uuid)
    : undefined;
  const canAddOptionTo = (order: TableOrder, line: OrderLine): boolean => {
    if (!line.uuid || !line.product_uuid || line.is_cart_option || line.addon_of_item_uuid) return false;
    if (currentData?.ordering_locked || order.type !== 'dine_in' || order.is_held) return false;
    if (!ADDON_OPEN_STATUSES.has(order.online_status)) return false;
    const product = productsById.get(line.product_uuid);
    return !!product && addableGroups(product, line.qte, carriedByLine(toSentLine(line), sentLines)).length > 0;
  };

  async function sendOptionAddons(payload: AddOptionsPayload) {
    if (!addOptionsTarget?.line.uuid || !guestToken) return;
    const { onlineStatus } = await postOptionAddons({
      restaurantUuid: restaurantUuid ?? '',
      guestToken,
      itemUuid: addOptionsTarget.line.uuid,
      plates: payload.plates,
      options: payload.options,
      idempotencyKey: addOptionsTarget.idempotencyKey,
    }).unwrap();
    setAddOptionsTarget(null);
    setAddOptionsSentKey(onlineStatus === 'pending_validation' ? 'addOptions.sentPending' : 'addOptions.sent');
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="left"
      width="380px"
      ariaLabel={t('tablePanel.ariaLabel')}
    >
      {/* Header */}
      <div className="px-4 py-2 border-b-2 border-ink flex items-center justify-between">
        <h2 className="font-display text-xl">{t('tablePanel.title')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label={t('tablePanel.close')}
        >
          ×
        </button>
      </div>

      {/* Session meta */}
      <div className="px-4 py-2 border-b border-ink/20 font-mono text-sm text-ink/70">
        {t('tablePanel.tableMeta', { number: tableNumber, covers })}
      </div>

      {/* BuffetHud banner */}
      {isBuffet && (
        <div className="px-4 pt-2">
          <BuffetHud
            roundCount={roundCount}
            cooldownSecondsRemaining={cooldown}
          />
        </div>
      )}

      {/* Rounds list */}
      <div ref={roundsListRef} className="flex-1 overflow-y-auto px-4 py-2 space-y-4">
        {sortedRounds.map((roundNumber) => {
          const roundOrders = roundMap.get(roundNumber)!;
          return (
            <div key={roundNumber}>
              <p className="font-display text-base mb-1">
                {t('tablePanel.roundLabel', { n: roundNumber })}
              </p>
              <div className="space-y-2">
                {roundOrders.map((order) => {
                  const { labelKey, variant } = resolveStatus(order.online_status);
                  const statusClass =
                    variant === 'rejected'
                      ? 'text-red-600 font-semibold'
                      : variant === 'pending'
                      ? 'text-amber-600'
                      : 'text-ink/60';
                  return (
                    <div
                      key={order.uuid}
                      className="border border-ink/20 rounded p-2 bg-cream"
                    >
                      {order.guest_nickname && (
                        <p className="font-mono text-sm font-medium mb-1">
                          {order.guest_nickname}
                        </p>
                      )}
                      <p className={`font-mono text-xs mb-1 ${statusClass}`}>
                        {t(labelKey)}
                      </p>
                      <ul className="space-y-0.5">
                        {order.lines.map((line, idx) => {
                          const lineCents = priceToCents(line.total_ttc);
                          const optionLabels = line.options
                            .map((option) => {
                              const name = option.name ?? option.label ?? '';
                              if (!name) return '';
                              const qty = Number(option.qty ?? 1);
                              return qty > 1 ? `${qty}× ${name}` : name;
                            })
                            .filter(Boolean);
                          return (
                            <li
                              key={idx}
                              className="font-mono text-xs"
                            >
                              <div className="flex justify-between gap-2">
                                <span>
                                  {line.qte}&times; {line.name}
                                </span>
                                {/* Hide prices in buffet mode */}
                                {!isBuffet && (
                                  <Price amountCents={lineCents} />
                                )}
                              </div>
                              {optionLabels.length > 0 && (
                                <ul className="list-none m-0 pl-4 text-ink/65">
                                  {optionLabels.map((label, optionIndex) => (
                                    <li key={optionIndex}>+ {label}</li>
                                  ))}
                                </ul>
                              )}
                              {line.comment && (
                                <p className="m-0 pl-4 italic text-ink/60">“{line.comment}”</p>
                              )}
                              {canAddOptionTo(order, line) && (
                                <button
                                  type="button"
                                  data-testid={`table-add-option-${line.uuid}`}
                                  aria-label={t('addOptions.actionAria', { name: line.name })}
                                  data-fx-mute
                                  onClick={() => {
                                    setAddOptionsSentKey(null);
                                    setAddOptionsTarget({ line, idempotencyKey: uuid() });
                                  }}
                                  className="min-h-[44px] ps-0 pe-3 bg-transparent border-0 cursor-pointer text-inherit text-start font-mono text-sm font-bold underline underline-offset-2"
                                >
                                  {t('addOptions.action')}
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {addOptionsSentKey && (
        <p
          data-testid="table-add-option-sent"
          role="status"
          className="m-0 px-4 py-2 bg-cream font-mono text-sm font-bold"
        >
          {t(addOptionsSentKey)}
        </p>
      )}

      {/* Running total — ordering mode only. Use the i18n key with a blank amount
          so the label text is translated; the formatted price lives in <Price>. */}
      {!isBuffet && runningTotalCents > 0 && (
        <div className="border-t-2 border-ink px-4 py-2 bg-paper flex justify-between font-display text-base">
          <span>{t('tablePanel.runningTotal', { amount: '' })}</span>
          <Price amountCents={runningTotalCents} />
        </div>
      )}

      {/* Le paiement autonome suit le total, jamais au-dessus : on ne propose
          pas de payer avant d'avoir montré combien. Rendu aussi en buffet —
          le forfait d'entrée est dû lui aussi, et c'est le serveur qui décide
          si la porte est ouverte. */}
      {guestToken && (
        <TablePaymentSection
          guestToken={guestToken}
          tableToken={session.tableToken}
          active={open && isVisible}
          mode={mode}
        />
      )}
      {addOptionsTarget && addOptionsProduct && (
        <AddOptionsSheet
          product={addOptionsProduct}
          dishName={addOptionsTarget.line.name}
          plateCount={addOptionsTarget.line.qte}
          carried={carriedByLine(toSentLine(addOptionsTarget.line), sentLines)}
          isBuffetDineIn={isBuffet}
          mode="dine_in"
          onCancel={() => setAddOptionsTarget(null)}
          onSubmit={sendOptionAddons}
        />
      )}
    </Drawer>
  );
}
